// RealPickleballCore_v8c_shaderholes_glow.tsx
// Shader-holes with: (1) darker back faces, (2) softer base color, (3) subtle emissive,
// and (4) a lightweight Bloom pass for a gentle glow.
//
// deps:
//   yarn add three @react-three/fiber @react-three/drei @react-three/postprocessing postprocessing

import React, { useMemo, useRef, useEffect } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";

export type RealPickleballCoreProps = {
  radius?: number;
  holes?: number;
  holeRadius?: number; // world units
  baseHex?: string;    // softened Franklin yellow
  emissiveHex?: string;
  emissiveIntensity?: number; // 0..1
  backfaceDarken?: number;    // 0..1 (multiplier on back faces)
  ambient?: number;           // 0..1 ambient factor
};

function fibonacciDirections(n: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / Math.max(1, n - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = golden * i;
    out.push(new THREE.Vector3(Math.cos(theta) * r, y, Math.sin(theta) * r).normalize());
  }
  return out;
}

const MAX_HOLES = 64;

const vertex = /* glsl */`
  varying vec3 vWorldPos;
  varying vec3 vNormalW;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    // transform normal to world
    vNormalW = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const fragment = /* glsl */`
  varying vec3 vWorldPos;
  varying vec3 vNormalW;

  uniform vec3 uBaseColor;
  uniform vec3 uEmissiveColor;
  uniform float uEmissiveIntensity;
  uniform float uRadius;            // sphere radius (world)
  uniform float uHoleRadius;        // hole radius (world)
  uniform int uHoleCount;           // number of active hole dirs
  uniform vec3 uHoleDirs[${MAX_HOLES}];
  uniform float uBackfaceDarken;    // 0..1 multiplier for back faces
  uniform vec3 uLightDir;           // normalized world-space dir (from surface toward light)
  uniform float uAmbient;           // 0..1 ambient term

  void main() {
    // Sphere normal from world normal
    vec3 N = normalize(vNormalW);

    // Holes via spherical caps
    float angThresh = uHoleRadius / uRadius;
    float cosThresh = cos(angThresh);
    for (int i = 0; i < ${MAX_HOLES}; i++) {
      if (i >= uHoleCount) break;
      float c = dot(N, normalize(uHoleDirs[i]));
      if (c >= cosThresh) discard;
    }

    // Simple Lambert lighting for depth (not PBR, but good enough for a ball)
    float lambert = max(dot(N, normalize(uLightDir)), 0.0);
    float lighting = clamp(uAmbient + lambert * 0.9, 0.0, 1.0);

    vec3 col = uBaseColor * lighting + uEmissiveColor * uEmissiveIntensity;

    // Darken back faces to keep interior readable through holes
    if (!gl_FrontFacing) {
      col *= uBackfaceDarken;
    }

    gl_FragColor = vec4(col, 1.0);
  }
`;

function ShaderBall({
  radius = 4,
  holes = 26,
  holeRadius = 0.34,              // slightly smaller cap for cleaner look
  baseHex = "#D6E24A",            // softened optic yellow
  emissiveHex = "#E8F766",
  emissiveIntensity = 0.18,       // subtle — the Bloom pass will pick this up
  backfaceDarken = 0.45,
  ambient = 0.25,
}: RealPickleballCoreProps) {
  const materialRef = useRef<THREE.ShaderMaterial>(null!);

  const holeDirs = useMemo(() => {
    const dirs = fibonacciDirections(Math.min(holes, MAX_HOLES));
    while (dirs.length < MAX_HOLES) dirs.push(new THREE.Vector3(1, 0, 0));
    return dirs;
  }, [holes]);

  const uniforms = useMemo(
    () => ({
      uBaseColor: { value: new THREE.Color(baseHex) },
      uEmissiveColor: { value: new THREE.Color(emissiveHex) },
      uEmissiveIntensity: { value: emissiveIntensity },
      uRadius: { value: radius },
      uHoleRadius: { value: holeRadius },
      uHoleCount: { value: Math.min(holes, MAX_HOLES) },
      uHoleDirs: { value: holeDirs.map((v) => v.clone()) as unknown as THREE.Vector3[] },
      uBackfaceDarken: { value: backfaceDarken },
      uLightDir: { value: new THREE.Vector3(0.5, 1.0, 0.7).normalize() },
      uAmbient: { value: ambient },
    }),
    [baseHex, emissiveHex, emissiveIntensity, radius, holeRadius, holes, holeDirs, backfaceDarken, ambient]
  );

  // Keep hole uniforms in sync
  useEffect(() => {
    if (!materialRef.current) return;
    (materialRef.current.uniforms.uHoleCount.value as number) = Math.min(holes, MAX_HOLES);
    const arr = materialRef.current.uniforms.uHoleDirs.value as THREE.Vector3[];
    for (let i = 0; i < MAX_HOLES; i++) {
      const src = i < holes ? holeDirs[i] : new THREE.Vector3(1, 0, 0);
      arr[i].copy(src);
    }
  }, [holes, holeDirs]);

  return (
    <mesh castShadow receiveShadow>
      <sphereGeometry args={[radius, 128, 128]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertex}
        fragmentShader={fragment}
        uniforms={uniforms}
        side={THREE.DoubleSide}
        depthTest={true}
        depthWrite={true}
        transparent={false}
      />
    </mesh>
  );
}

export function RealPickleballCore(props: RealPickleballCoreProps) {
  return <ShaderBall {...props} />;
}

export function RealPickleballDemo() {
  return (
    <div className="w-full h-[620px] bg-black">
      <Canvas
        shadows
        gl={{ logarithmicDepthBuffer: true, antialias: true }}
        camera={{ position: [0, 0, 14], near: 0.01, far: 5000 }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[8, 12, 10]} intensity={0.9} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} />

        {/* Ball */}
        <RealPickleballCore />

        {/* World */}
        <Environment preset="city" />
        <OrbitControls enableDamping dampingFactor={0.08} />

        {/* Subtle glow */}
        <EffectComposer>
          <Bloom luminanceThreshold={0.15} luminanceSmoothing={0.08} intensity={0.15} mipmapBlur />
        </EffectComposer>
      </Canvas>
    </div>
  );
}

// Usage:
// import { RealPickleballDemo, RealPickleballCore } from './RealPickleballCore_v8c_shaderholes_glow';
// <RealPickleballDemo />
